import { Controller, useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import {
  FormControl,
  FormErrorMessage,
  InputRightElement,
} from '@chakra-ui/react';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import useCompanyContext from 'common/hooks/useCompanyContext';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import Icon from 'components/_core/Icon';
import DatePicker from 'components/LegacyInput/DatePicker';

import Header from '../../components/EpAgreementWallHeader';
import { GetEpAgreementWallContextQuery } from '../../graphql/queries/getEpAgreementWallContext.gql';
import { ReactDatePickerStyle } from './datePickerStyles';
import { SetRegistrationDateDocument } from './graphql/mutations/setRegistrationDate.gql';
import locales from './locales';

interface IntroductionProps {
  moduleData: GetEpAgreementWallContextQuery;
  onNext: () => void;
}

type FormData = {
  registrationDate: string;
};

const RegistrationDate = asBentoPage<IntroductionProps>(
  ({ Layout, moduleData, onNext, onPrev }) => {
    const { companyProfileId } = useCompanyContext();

    if (!companyProfileId) {
      throw Error('companyProfileId is undefined in RegistrationDate');
    }

    const {
      control,
      formState: { errors },
      handleSubmit,
    } = useForm<FormData>();

    const [setRegistrationDateMutation, { loading }] = useMutation(
      SetRegistrationDateDocument,
      {
        onError: (error) => {
          throw error;
        },
      },
    );

    const onSubmit = async (data: FormData) => {
      await setRegistrationDateMutation({
        variables: {
          company: {
            companyProfileId,
            registrationDate: data.registrationDate,
          },
        },
      });

      onNext();
    };

    return (
      <Layout header={<Header onPrev={onPrev} />}>
        <Heading>{locales.title}</Heading>

        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl isInvalid={Boolean(errors.registrationDate)}>
            <Controller
              control={control}
              defaultValue={
                moduleData.viewer.company.profile.registrationDate || ''
              }
              name="registrationDate"
              render={({ field }) => (
                <ReactDatePickerStyle
                  hasError={Boolean(errors.registrationDate)}
                >
                  <DatePicker
                    id="registration-date"
                    max={new Date().toISOString()}
                    onValueChange={(date) => field.onChange(date)}
                    placeholderText="DD/MM/YYYY"
                    value={field.value}
                  />

                  <InputRightElement
                    height="100%"
                    paddingRight="15px"
                    position="absolute"
                  >
                    <Icon name="calendar" size="icon-18" />
                  </InputRightElement>
                </ReactDatePickerStyle>
              )}
              rules={{ required: locales.error }}
            />

            <FormErrorMessage>
              {errors.registrationDate?.message}
            </FormErrorMessage>
          </FormControl>

          <Button
            isExpanded
            isLoading={loading}
            marginTop="space-24"
            type="submit"
          >
            {locales.cta}
          </Button>
        </form>
      </Layout>
    );
  },
);

export default RegistrationDate;
